/**
 * Record
 *
 * @typedef {Object} Record
 * @property {HeaderWrapper} headerWrapper
 * @property {HeaderWrapperEncryptionParams} headerWrapperEncryptionParams
 * @property {Block[]} blocks - consists of the data and files keys only. the header is stored inside the encrypted headerWrapper.
 * @protected
 * @example
 *  const record = {
 *    headerWrapper: {
 *      metadata: {
 *        type: 'standard',
 *        id: 'id',
 *        creator: 'creator',
 *        personalPage: 'personalPage',
 *        signature: 'signature',
 *        signatureKeyVersion: 'signatureKeyVersion',
 *        timestamp: 1488992366155,
 *        timezoneOffset: 120,
 *        isOnce: false,
 *        expiration: 1488992300000,
 *        subject: 'subject',
 *        origin: 'origin',
 *        custom: {
 *          key1: value1,
 *          key2: value2,
 *          ...
 *        }
 *      },
 *      blocks: [
 *        {
 *          symmetricEncryption: [
 *            {
 *              hint: 'hint',
 *              derivationParams: {
 *                iterations: 'iterations',
 *                salt: 'salt'
 *              },
 *              encryptionParams: {
 *                iv: 'iv',
 *                mode: 'gcm',
 *                ts: 128,
 *                adata: 'cryptolize'
 *              },
 *              encryptedKey: 'encryptedKey'
 *            },
 *            ...
 *          ],
 *          asymmetricEncryption: [
 *            {
 *              id: 'id',
 *              version: 'version',
 *              tag: 'tag',
 *              encryptionParams: {
 *                iv: 'iv',
 *                mode: 'gcm',
 *                ts: 128,
 *                adata: 'cryptolize'
 *              },
 *              encryptedKey: 'encryptedKey'
 *            },
 *            ...
 *          ],
 *          dataEncryptionParams: {
 *            iv: 'iv',
 *            mode: 'gcm',
 *            ts: 128,
 *            adata: 'cryptolize'
 *          },
 *          filesEncryptionParams: [
 *            {
 *              id: 'id',
 *              encryptionParams: {
 *                iv: 'iv',
 *                mode: 'gcm',
 *                ts: 128,
 *                adata: 'cryptolize'
 *              }
 *            },
 *            ...
 *          ],
 *          filesStorageParams: [
 *            {
 *              id: 'id',
 *              path: 'path',
 *              service: 'service'
 *            },
 *            ...
 *          ]
 *        }
 *        ...
 *      ]
 *    },
 *    headerWrapperEncryptionParams: {
 *      id: 'id',
 *      tag: 'tag',
 *      encryptionParams: {
 *        iv: 'iv',
 *        mode: 'gcm',
 *        ts: 128,
 *        adata: 'cryptolize'
 *      },
 *    },
 *    blocks: [
 *      {
 *        data: {
 *          text: 'text',
 *          files: [
 *            {
 *              name: 'name',
 *              size: 'size',
 *              type: 'type',
 *              id: 'id'
 *            },
 *            ...
 *          ]
 *        },
 *        files: [
 *          {
 *            id: 'id',
 *            data: 'data'
 *          },
 *          ...
 *        ]
 *      },
 *      ...
 *    ]
 *  }
 */

/**
 * Create record
 *
 * @param {HeaderWrapper} headerWrapper
 * @param {HeaderWrapperEncryptionParams} headerWrapperEncryptionParams
 * @param {Block[]} blocks
 * @return {Record}
 * @private
 */
export function createRecord (headerWrapper, headerWrapperEncryptionParams, blocks) {
  return {
    headerWrapper,
    headerWrapperEncryptionParams,
    blocks
  }
}

/**
 * Header wrapper
 *
 * @typedef {Object} HeaderWrapper
 * @property {RecordMetadata} metadata
 * @property {Header[]} blocks
 * @protected
 */

/**
 * Create header wrapper
 *
 * @param {RecordMetadata} metadata
 * @param {Header[]} blocks
 * @return {HeaderWrapper}
 * @private
 */
export function createHeaderWrapper (metadata, blocks) {
  return {
    metadata,
    blocks
  }
}

/**
 * RecordMetadata
 *
 * @typedef {Object} RecordMetadata
 * @property {String} type
 * @property {String} id
 * @property {String} [creator]
 * @property {String} [personalPage]
 * @property {Base64UrlString} [signature]
 * @property {String} [signatureKeyVersion]
 * @property {Number} timestamp
 * @property {Number} timezoneOffset
 * @property {Boolean} isOnce
 * @property {Number} [expiration]
 * @property {String} [subject]
 * @property {String} [origin]
 * @property {Object} [custom]
 * @protected
 */

/**
 * Create record metadata
 *
 * @param {String} id
 * @param {String} [creator]
 * @param {String} [personalPage]
 * @param {Base64UrlString} [signature]
 * @param {String} [signatureKeyVersion]
 * @param {Number} timestamp
 * @param {Number} timezoneOffset
 * @param {Boolean} isOnce
 * @param {Number} [expiration]
 * @param {String} [subject]
 * @param {String} [origin]
 * @param {Object} [custom]
 * @return {RecordMetadata}
 * @public
 */
export function createRecordMetadata (id, creator, personalPage, signature, signatureKeyVersion, timestamp, timezoneOffset, isOnce, expiration, subject, origin, custom) {
  return {
    type: 'standard',
    id,
    creator,
    personalPage,
    signature,
    signatureKeyVersion,
    timestamp,
    timezoneOffset,
    isOnce,
    expiration,
    subject,
    origin,
    custom
  }
}

/**
 * Header wrapper encryption params
 *
 * @typedef {Object} HeaderWrapperEncryptionParams
 * @property {String} keyId
 * @property {Base64UrlString} tag
 * @property {SymmetricEncryptionParams} encryptionParams
 * @protected
 */

/**
 * Create header wrapper encryption params
 *
 * @param {String} id
 * @param {Base64UrlString} tag
 * @param {SymmetricEncryptionParams} encryptionParams
 * @return {HeaderEncryptionWrapper}
 * @private
 */
export function createHeaderWrapperEncryptionParams (id, tag, encryptionParams) {
  return {
    id,
    tag,
    encryptionParams
  }
}

/**
 * Block
 *
 * @typedef {Object} Block
 * @property {Header} header
 * @property {Data} data encrypted and represented by Base64UrlString
 * @property {EncryptedFile[]} files
 * @protected
 */

/**
 * Create block
 *
 * @param {Header} header
 * @param {Data} data
 * @param {EncryptedFile[]} files
 * @return {Block}
 * @private
 */
export function createBlock (header, data, files) {
  return {
    header,
    data,
    files
  }
}

/**
 * Header
 *
 * @typedef {Object} Header
 * @property {SymmetricEncryptionWrapper[]} symmetricEncryption
 * @property {AsymmetricEncryptionWrapper[]} asymmetricEncryption
 * @property {SymmetricEncryptionParams} encryptionParams
 * @property {FilesEncryptionParams[]} filesEncryptionParams
 * @property {FilesStorageParams[]} filesStorageParams
 * @protected
 */

/**
 * Create header
 *
 * @param {SymmetricEncryptionWrapper[]} symmetricEncryption
 * @param {AsymmetricEncryptionWrapper[]} asymmetricEncryption
 * @param {SymmetricEncryptionParams} dataEncryptionParams
 * @param {FilesEncryptionParams[]} filesEncryptionParams
 * @param {FilesStorageParams[]} filesStorageParams
 * @return {Header}
 * @private
 */
export function createHeader (symmetricEncryption, asymmetricEncryption, dataEncryptionParams, filesEncryptionParams, filesStorageParams) {
  return {
    symmetricEncryption,
    asymmetricEncryption,
    dataEncryptionParams,
    filesEncryptionParams,
    filesStorageParams
  }
}

/**
 * Symmetric encryption wrapper
 *
 * @typedef {Object} SymmetricEncryptionWrapper
 * @property {String} hint
 * @property {DerivationParams} derivationParams
 * @property {SymmetricEncryptionParams} encryptionParams
 * @property {Base64UrlString} encryptedKey
 * @protected
 */

/**
 * Create symmetric encryption wrapper
 *
 * @param {String} hint
 * @param {DerivationParams} derivationParams
 * @param {SymmetricEncryptionParams} encryptionParams
 * @param {Base64UrlString} encryptedKey
 * @return {SymmetricEncryptionWrapper}
 * @private
 */
export function createSymmetricEncryptionWrapper (hint, derivationParams, encryptionParams, encryptedKey) {
  return {
    hint,
    derivationParams,
    encryptionParams,
    encryptedKey
  }
}

/**
 * Asymmetric encryption wrapper
 *
 * @typedef {Object} AsymmetricEncryptionWrapper
 * @property {String} id
 * @property {String} version
 * @property {Base64UrlString} tag
 * @property {SymmetricEncryptionParams} encryptionParams
 * @property {Base64UrlString} encryptedKey
 * @protected
 */

/**
 * Create asymmetric encryption wrapper
 *
 * @param {String} id
 * @param {String} version
 * @param {Base64UrlString} tag
 * @param {SymmetricEncryptionParams} encryptionParams
 * @param {Base64UrlString} encryptedKey
 * @return {AsymmetricEncryptionWrapper}
 * @private
 */
export function createAsymmetricEncryptionWrapper (id, version, tag, encryptionParams, encryptedKey) {
  return {
    id,
    version,
    tag,
    encryptionParams,
    encryptedKey
  }
}

/**
 * Files encryption params
 *
 * @typedef {Object} FilesEncryptionParams
 * @property {String} id
 * @property {SymmetricEncryptionParams} encryptionParams
 * @protected
 */

/**
 * Create files encryption params
 *
 * @param {String} id
 * @param {SymmetricEncryptionParams} encryptionParams
 * @return {FilesEncryptionParams}
 * @private
 */
export function createFilesEncryptionParams (id, encryptionParams) {
  return {
    id,
    encryptionParams
  }
}

/**
 * Files storage params
 *
 * @typedef {Object} FilesStorageParams
 * @property {String} id
 * @property {String} path
 * @property {String} service
 * @protected
 */

/**
 * Create files storage params
 *
 * @param {String} id
 * @param {String} path
 * @param {String} service
 * @return {FilesStorageParams}
 * @private
 */
export function createFilesStorageParams (id, path, service) {
  return {
    id,
    path,
    service
  }
}

/**
 * Data
 *
 * @typedef {Object} Data
 * @property {String} text
 * @property {FileMetadata[]} files
 * @protected
 */

/**
 * Create Data
 *
 * @param {String} text
 * @param {FileMetadata[]} files
 * @return {Data}
 * @private
 */
export function createData (text, files) {
  return {
    text,
    files
  }
}

/**
 * File metadata
 *
 * @typedef {Object} FileMetadata
 * @property {String} name
 * @property {Number} size
 * @property {String} type
 * @property {String} id
 * @protected
 */

/**
 * Create file metadata
 *
 * @param {String} name
 * @param {Number} size
 * @param {String} type
 * @param {String} id
 * @return {FileMetadata}
 * @private
 */
export function createFileMetadata (name, size, type, id) {
  return {
    name,
    size,
    type,
    id
  }
}

/**
 * Encrypted file
 *
 * @typedef {Object} EncryptedFile
 * @property {String} id
 * @property {String} path
 * @property {String} service
 * @property {ArrayBuffer} data
 * @protected
 */

/**
 * Create file
 *
 * @param {String} id
 * @param {String} path
 * @param {String} service
 * @param {ArrayBuffer} data
 * @return {EncryptedFile}
 * @private
 */
export function createEncryptedFile (id, path, service, data) {
  return {
    id,
    path,
    service,
    data
  }
}

/**
 * Symmetric encryption params
 *
 * @typedef {Object} SymmetricEncryptionParams
 * @property {Base64UrlString} iv
 * @property {String} mode
 * @property {Number} ts
 * @property {String} adata
 * @protected
 */

/**
 * Create encryption params
 *
 * @param {Base64UrlString} iv
 * @return {SymmetricEncryptionParams}
 * @public
 */
export function createEncryptionParams (iv) {
  return {
    iv,
    mode: 'gcm',
    ts: 128,
    adata: 'cryptolize'
  }
}

/**
 * Derivation params
 *
 * @typedef {Object} DerivationParams
 * @property {Number} iterations
 * @property {Base64UrlString} salt
 * @protected
 */

/**
 * Create derivation params
 *
 * @param {Number} iterations
 * @param {Base64UrlString} salt
 * @return {DerivationParams}
 * @private
 */
export function createDerivationParams (iterations, salt) {
  return {
    iterations,
    salt
  }
}



// WEBPACK FOOTER //
// ./src/record/format.js